import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Calculator, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const fmtUSD = (n) => (n == null ? "—" : `$${Math.round(Number(n)).toLocaleString()}`);
const fmtPct = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);

/**
 * Comp → Portfolio Gap dialog.
 *
 * Given a comp (Library row, staged LoopNet/Crexi row, or ad-hoc $/sqft + cap),
 * shows what each of your owned properties would be worth if priced like the
 * comp, and the dollar/percent gap to its current valuation.
 *
 * Methodology:
 *   • Comp $/sqft × my sqft           → "value if priced like comp's $/sqft"
 *   • My NOI ÷ comp cap rate          → "value if priced at comp's cap rate"
 *   • Both available → 50/50 blend.
 */
export default function CompGapDialog({ open, onOpenChange, comp }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [marketFilter, setMarketFilter] = useState("");

  useEffect(() => {
    if (!open || !comp) return;
    setMarketFilter("");
    fetchGap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, comp?.id, comp?.price_per_sqft]);

  const fetchGap = async (filter) => {
    if (!comp) return;
    setLoading(true);
    try {
      const payload = {
        comp_id: comp.id,
        price_per_sqft: comp.price_per_sqft || null,
        cap_rate: comp.cap_rate || null,
        market_filter: filter || null,
      };
      const r = await api.post("/comps/gap", payload);
      setResult(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not compute gap");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  if (!open || !comp) return null;

  const totalGap = result?.totals?.gap;
  const totalGapPct = result?.totals?.gap_pct;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="comp-gap-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-emerald-600" /> Portfolio gap vs this comp
          </DialogTitle>
        </DialogHeader>

        <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 mb-4">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Comp basis</div>
          <div className="text-sm font-semibold text-slate-900 truncate">{comp.formatted_address || comp.address}</div>
          <div className="mt-2 flex flex-wrap gap-4 text-sm">
            <div><span className="overline text-slate-500">$/sqft:</span> <span className="font-mono font-bold ml-1">{comp.price_per_sqft ? `$${Math.round(comp.price_per_sqft).toLocaleString()}` : "—"}</span></div>
            <div><span className="overline text-slate-500">Cap:</span> <span className="font-mono font-bold ml-1">{fmtPct(comp.cap_rate)}</span></div>
            <div><span className="overline text-slate-500">Source:</span> <span className="font-mono ml-1 capitalize">{comp.source || "—"}</span></div>
          </div>
        </div>

        {/* Optional market filter */}
        <div className="flex items-center gap-2 mb-3">
          <input
            className="input-clean text-sm flex-1"
            placeholder="Optional — filter your portfolio by market (e.g. Charlotte MSA)"
            value={marketFilter}
            onChange={(e) => setMarketFilter(e.target.value)}
            data-testid="gap-market-filter"
          />
          <button onClick={() => fetchGap(marketFilter)} className="btn-outline-dark text-sm" data-testid="gap-recompute">Recompute</button>
        </div>

        {/* Totals */}
        {result?.totals && (result.totals.current_value || result.totals.comp_value) && (
          <div className="grid grid-cols-3 gap-3 mb-4" data-testid="gap-totals">
            <Kpi label="Current portfolio value" value={fmtUSD(result.totals.current_value)} />
            <Kpi label="Comp-implied value" value={fmtUSD(result.totals.comp_value)} accent="emerald" />
            <Kpi
              label="Gap"
              value={totalGap == null ? "—" : `${totalGap >= 0 ? "+" : ""}${fmtUSD(totalGap)}`}
              sub={totalGapPct == null ? "" : `${totalGapPct >= 0 ? "+" : ""}${totalGapPct}%`}
              accent={totalGap == null ? "slate" : totalGap > 0 ? "emerald" : "rose"}
            />
          </div>
        )}

        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-3 py-2 overline text-slate-600">Property</th>
                <th className="px-3 py-2 overline text-slate-600 text-right">Sqft</th>
                <th className="px-3 py-2 overline text-slate-600 text-right">NOI</th>
                <th className="px-3 py-2 overline text-slate-600 text-right">Current</th>
                <th className="px-3 py-2 overline text-slate-600 text-right">If $/sqft</th>
                <th className="px-3 py-2 overline text-slate-600 text-right">If cap</th>
                <th className="px-3 py-2 overline text-slate-600 text-right">Comp value</th>
                <th className="px-3 py-2 overline text-slate-600 text-right">Gap</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-12 text-slate-400">Computing…</td></tr>}
              {!loading && (!result?.rows || result.rows.length === 0) && (
                <tr><td colSpan={8} className="text-center py-12 text-slate-400">No properties to compare.</td></tr>
              )}
              {result?.rows?.map((r) => {
                const gap = r.gap;
                const trend = gap == null ? null : gap > 1000 ? "up" : gap < -1000 ? "down" : "flat";
                return (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0" data-testid={`gap-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-slate-900">{r.name}</div>
                      <div className="text-[10px] text-slate-400 truncate max-w-xs">{r.address}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{r.sqft ? r.sqft.toLocaleString() : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtUSD(r.noi)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtUSD(r.current_value)}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-500">{fmtUSD(r.comp_value_pps)}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-500">{fmtUSD(r.comp_value_cap)}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{fmtUSD(r.comp_value)}</td>
                    <td className={`px-3 py-2 text-right font-mono font-bold ${trend === "up" ? "text-emerald-700" : trend === "down" ? "text-rose-700" : "text-slate-500"}`}>
                      <div className="inline-flex items-center gap-1 justify-end">
                        {trend === "up" ? <TrendingUp className="w-3.5 h-3.5" /> : trend === "down" ? <TrendingDown className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                        {gap == null ? "—" : `${gap >= 0 ? "+" : ""}${fmtUSD(gap)}`}
                      </div>
                      {r.gap_pct != null && (
                        <div className="text-[10px] font-mono text-slate-400">{r.gap_pct >= 0 ? "+" : ""}{r.gap_pct}%</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <DialogFooter className="mt-4">
          <button type="button" data-testid="gap-close-btn" onClick={() => onOpenChange(false)} className="btn-primary-dark">Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value, sub, accent = "slate" }) {
  const colors = {
    slate: "border-slate-200 bg-white text-slate-900",
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-900",
    rose: "border-rose-300 bg-rose-50 text-rose-900",
  };
  return (
    <div className={`rounded-sm border p-3 ${colors[accent] || colors.slate}`}>
      <div className="overline text-slate-500">{label}</div>
      <div className="text-xl font-bold font-mono mt-1">{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
